import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';

interface AllDocumentsListProps {
    onDocumentAction?: (documentType: string) => void;
    facilityId?: string; // Add facilityId prop
}

const AllDocumentsList: React.FC<AllDocumentsListProps> = ({ facilityId }) => {
    const [loadingState, setLoadingState] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [currentFacilityId, setCurrentFacilityId] = useState<string | null>(null);

    // Get facilityId from props or URL params
    useEffect(() => {
        if (facilityId) {
            setCurrentFacilityId(facilityId);
        } else {
            // Try to get from URL params if not provided via props
            const urlParams = new URLSearchParams(window.location.search);
            const urlFacilityId = urlParams.get('facilityId');
            if (urlFacilityId) {
                setCurrentFacilityId(urlFacilityId);
            }
        }
    }, [facilityId]);

    const documents = [
        { key: 'master_facility_agreement', title: 'Master Facility Agreement' },
        { key: 'term_sheet', title: 'Term Sheet' },
        { key: 's1_registration', title: 'S-1 Registration' },
        { key: 'deal_documents', title: 'Deal Documents' },


        // { key: 'registration_rights_agreement', title: 'Registration Rights Agreement' },
        // { key: 'secretary_certificate', title: 'Secretary Certificate' },

        // { key: 's1_legal_review', title: 'S-1 Legal Review' },
        // { key: 'form_8k', title: '8-K Form' },
        // { key: 'press_release', title: 'Press Release' },
        // { key: 'transfer_agent_package', title: 'Transfer Agent Package' },
        // { key: 'exercise_notice', title: 'Exercise Notice' },x
        // { key: 'settlement_statement', title: 'Settlement Statement' },
        // { key: 'wire_confirmation', title: 'Wire Confirmation' },
        // { key: 'legal_opinion', title: 'Legal Opinion' },
        // { key: 'officer_certificate', title: 'Officer Certificate' }
    ];

    const handleDownloadButtonClick = async (documentKey: string, documentTitle: string) => {
        setLoadingState(documentKey);
        setErrorMessage(null);

        switch (documentKey) {
            case 'term_sheet':
                try {
                    if (!currentFacilityId) {
                        throw new Error('Facility ID is required to download term sheet');
                    }

                    const response = await axios.get(
                        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/signature/download-term-sheet/${currentFacilityId}`,
                        {
                            headers: {
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                            },
                            responseType: 'blob' // Changed back to 'blob' from 'arraybuffer'
                        }
                    );
                    
                    // Don't force PDF type - let the backend determine the content type
                    const blob = response.data;
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    
                    // Extract filename from response headers if available
                    const contentDisposition = response.headers['content-disposition'];
                    let filename = `TermSheet_${currentFacilityId}.docx`; // Default to DOCX
                    if (contentDisposition) {
                        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                        if (filenameMatch) {
                            filename = filenameMatch[1];
                        }
                    }
                    
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                    setLoadingState(null);
                    
                } catch (error: any) {
                    console.error('Error downloading term sheet:', error);
                    
                    // Enhanced error handling
                    let errorMessage = 'Failed to download term sheet';
                    
                    if (error.response) {
                        if (error.response.status === 404) {
                            errorMessage = 'Term sheet not found for this facility';
                        } else if (error.response.status === 403) {
                            errorMessage = 'You do not have permission to download this term sheet';
                        } else {
                            errorMessage = `Server error (${error.response.status})`;
                        }
                    } else if (error.message) {
                        errorMessage = error.message;
                    }
                    
                    setErrorMessage(`Error downloading ${documentTitle}: ${errorMessage}`);
                    setLoadingState(null);
                }
                break;

            case 'form_8k':
                try {
                    // Handle 8-K form download
                    const response = await axios.get(
                        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/8k-generation/requestId/download/type`,
                        {
                            headers: {
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                            },
                            responseType: 'blob'
                        }
                    );
                    
                    const blob = response.data;
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${documentTitle}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                    setLoadingState(null);
                } catch (error: any) {
                    setErrorMessage(`Error downloading ${documentTitle}: ${error.message}`);
                    setLoadingState(null);
                }
                break;

            case 'deal_documents':
                try {
                    if (!currentFacilityId) {
                        throw new Error('Facility ID is required to download deal documents');
                    }

                    const response = await axios.get(
                        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/signature/download-docx/${currentFacilityId}`,
                        {
                            headers: {
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                            },
                            responseType: 'blob'
                        }
                    );
                    
                    const blob = response.data;
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    
                    // Extract filename from response headers if available
                    const contentDisposition = response.headers['content-disposition'];
                    let filename = `FacilityAgreement_${currentFacilityId}.docx`; // Default filename
                    if (contentDisposition) {
                        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                        if (filenameMatch) {
                            filename = filenameMatch[1];
                        }
                    }
                    
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                    setLoadingState(null);
                    
                } catch (error: any) {
                    console.error('Error downloading deal documents:', error);
                    
                    let errorMessage = 'Failed to download deal documents';
                    
                    if (error.response) {
                        if (error.response.status === 404) {
                            errorMessage = 'Deal documents not found for this facility';
                        } else if (error.response.status === 403) {
                            errorMessage = 'You do not have permission to download these documents';
                        } else {
                            errorMessage = `Server error (${error.response.status})`;
                        }
                    } else if (error.message) {
                        errorMessage = error.message;
                    }
                    
                    setErrorMessage(`Error downloading ${documentTitle}: ${errorMessage}`);
                    setLoadingState(null);
                }
                break;
            case 's1_registration':
                try {
                    // Handle S-1 Registration download
                    const response = await axios.get(
                        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/enhanced-s1/68ad5492f56ec8014cd928b5/download/draft`,
                        {
                            headers: {
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                            },
                            responseType: 'blob'
                        }
                    );

                    const blob = response.data;
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${documentTitle}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                    setLoadingState(null);
                } catch (error: any) {
                    setErrorMessage(`Error downloading ${documentTitle}: ${error.message}`);
                    setLoadingState(null);
                }
                break;
            case 'master_facility_agreement':

            try {
                if (!currentFacilityId) {
                    throw new Error('Facility ID is required to download master facility agreement');
                }

                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/signature/download-docx/${currentFacilityId}`,
                    {
                        headers: {
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                        },
                        responseType: 'blob'
                    }
                );
                
                const blob = response.data;
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                
                // Extract filename from response headers if available
                const contentDisposition = response.headers['content-disposition'];
                let filename = `MasterFacilityAgreement_${currentFacilityId}.docx`; // Default filename
                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                    if (filenameMatch) {
                        filename = filenameMatch[1];
                    }
                }
                
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                setLoadingState(null);
                
            } catch (error: any) {
                console.error('Error downloading master facility agreement:', error);
                
                let errorMessage = 'Failed to download master facility agreement';
                
                if (error.response) {
                    if (error.response.status === 404) {
                        errorMessage = 'Master facility agreement not found for this facility';
                    } else if (error.response.status === 403) {
                        errorMessage = 'You do not have permission to download this document';
                    } else {
                        errorMessage = `Server error (${error.response.status})`;
                    }
                } else if (error.message) {
                    errorMessage = error.message;
                }
                
                setErrorMessage(`Error downloading ${documentTitle}: ${errorMessage}`);
                setLoadingState(null);
            }
            break;
            default:
                // Handle other document types
                console.log(`Download not implemented for ${documentKey}`);
                setLoadingState(null);
                break;
        }
    };

    return (
        <div className="space-y-4">
            {errorMessage && (
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-red-50 border border-red-200 rounded-lg p-4"
                >
                    <p className="text-red-800 text-sm">{errorMessage}</p>
                    <button
                        onClick={() => setErrorMessage(null)}
                        className="mt-2 text-red-600 hover:text-red-800 text-xs underline"
                    >
                        Dismiss
                    </button>
                </motion.div>
            )}

            {documents.map((document, index) => (
                <motion.div
                    key={document.key}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">{document.title}</h3>
                        </div>
                        <div className="text-right">
                            <button
                                onClick={() => handleDownloadButtonClick(document.key, document.title)}
                                disabled={loadingState === document.key || (document.key === 'term_sheet' && !currentFacilityId)}
                                className="px-4 py-2 bg-green-200 text-green-800 rounded-lg hover:bg-green-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title={document.key === 'term_sheet' && !currentFacilityId ? 'Facility ID required' : ''}
                            >
                                {loadingState === document.key ? 'Downloading...' : 'Download'}
                            </button>
                        </div>
                    </div>
                </motion.div>
            ))}
        </div>
    );
};

export default AllDocumentsList;